import type { EnergyTypes, AggregationLevel } from '~/types/new/energy';

export const useChronologicalRepository = () => {
  const { client } = useApiV2();
  const { cancelRequestIfDoubled } = useCoreApiHelper();
  const {
    setConsumptionResponse,
    setConsumptionError,
    setLoadingConsumption,
    setProductionResponse,
    setProductionError,
    setLoadingProduction,
    setTemperatureResponse,
    setTemperatureError,
    setLoadingTemperature,
    setComparisonResponse,
    setComparisonError,
    setLoadingComparison,
  } = chronologicalStore();

  const getChronologicalConsumption = async (options: {
    energyType: EnergyTypes;
    start: string;
    end: string;
    aggregationLevel: 'minute' | 'hour' | 'day' | 'week' | 'month';
    areaId: number;
  }) => {
    await cancelRequestIfDoubled({
      requestId: 'explore-chronological-consumption',
      execute: async (signal) =>
        client.GET('/v2/areas/{area_id}/explore/consumptions', {
          params: {
            query: {
              energy_type: options.energyType,
              start: options.start,
              end: options.end,
              aggregation_level: options.aggregationLevel,
            },
            path: {
              area_id: options.areaId,
            },
          },
          signal: signal,
        }),
      onLoadingStart: () => {
        setLoadingConsumption(true);
      },
      onLoadingEnd: () => {
        setLoadingConsumption(false);
      },
      onError: (error) => {
        setConsumptionError(error);
      },
      onSuccess: (data) => {
        setConsumptionResponse(data);
        setConsumptionError(null);
      },
    });
  };

  const getChronologicalProduction = async (options: { energyType: EnergyTypes; areaId: number; start: string; end: string }) => {
    await cancelRequestIfDoubled({
      requestId: 'explore-chronological-production',
      execute: async (signal) =>
        client.GET('/v2/areas/{area_id}/explore/productions', {
          params: {
            query: {
              energy_type: options.energyType,
              start: options.start,
              end: options.end,
            },
            path: {
              area_id: options.areaId,
            },
          },
          signal: signal,
        }),
      onLoadingStart: () => {
        setLoadingProduction(true);
      },
      onLoadingEnd: () => {
        setLoadingProduction(false);
      },
      onError: (error) => {
        setProductionError(error);
      },
      onSuccess: (data) => {
        setProductionResponse(data);
        setProductionError(null);
      },
    });
  };

  const getChronologicalTemperature = async (options: { siteId: number; start: string; end: string }) => {
    await cancelRequestIfDoubled({
      requestId: 'explore-chronological-temperature',
      execute: async (signal) =>
        client.GET('/v2/sites/{site_id}/temperatures/', {
          params: {
            query: {
              start: options.start,
              end: options.end,
            },
            path: {
              site_id: options.siteId,
            },
          },
          signal: signal,
        }),
      onLoadingStart: () => {
        setLoadingTemperature(true);
      },
      onLoadingEnd: () => {
        setLoadingTemperature(false);
      },
      onError: (error) => {
        setTemperatureError(error);
      },
      onSuccess: (data) => {
        setTemperatureResponse(data);
        setTemperatureError(null);
      },
    });
  };

  const getComparisonConsumption = async (options: {
    energyType: EnergyTypes;
    areaId: number;
    start: string;
    end: string;
    areaToCompare: { id: number; start: string }[];
    aggregationLevel: AggregationLevel;
  }) => {
    await cancelRequestIfDoubled({
      requestId: 'explore-comparison-consumption',
      execute: async (signal) =>
        client.POST('/v2/areas/{area_id}/explore/compare', {
          params: {
            query: {
              energy_type: options.energyType,
              start: options.start,
              end: options.end,
              aggregation_level: options.aggregationLevel,
            },
            path: {
              area_id: options.areaId,
            },
          },
          body: options.areaToCompare.map((area) => ({
            area_id: area.id,
            start: area.start,
          })),
          signal: signal,
        }),
      onLoadingStart: () => {
        setLoadingComparison(true);
      },
      onLoadingEnd: () => {
        setLoadingComparison(false);
      },
      onError: (error) => {
        setComparisonError(error);
      },
      onSuccess: (data) => {
        setComparisonResponse(data);
        setComparisonError(null);
      },
    });
  };

  return { getChronologicalConsumption, getChronologicalProduction, getChronologicalTemperature, getComparisonConsumption };
};
